import { EhrVendor, getEhrVendor } from "Utils/EhrVendorMap";

/**
 * Document download straight from the EHR.
 *
 * Kept out of commonFetch because that helper assumes a JSON envelope and
 * unwraps `data`; this endpoint streams the file itself, so the response has to
 * be read as a blob.
 */

export type EhrDownloadResult = {
  success: boolean;
  blob?: Blob;
  contentType?: string;
  message?: string;
};

const bearer: () => string = () => {
  const localToken = localStorage.getItem("cm-token") || "";
  const token = localToken ? JSON.parse(localToken) : "";
  return token ? `Bearer ${token}` : "";
};

/**
 * Which vendor serves this practice.
 *
 * The capabilities response is the authority: the server reads it from
 * ehr_practicelocation_credentials.ehr_vendor for the patient actually on screen.
 *
 * PracticeEhrVendorMap is a hardcoded practiceLocationId -> vendor table, and a hardcoded table
 * cannot be right in more than one environment. On QA the EHR practice locations are 1011 (eCW)
 * and 1012 (Athena) while the table lists 1000 and 1003, so getEhrVendor returned null for every
 * real practice -- which silently removed the "Download EHR source" button and sent the view icon
 * to the local store instead of the EHR. For an Athena document that is fatal: the bytes only ever
 * existed in Athena, so the user got "has no readable file content" with no working action
 * anywhere on the row (CV-448).
 *
 * The map stays as a fallback for callers that have no capabilities response to hand, but a
 * caller that has one should always pass it.
 */
const resolveVendor: (
  practiceLocationId: number | null | undefined,
  vendor?: string | null
) => EhrVendor | null = (practiceLocationId, vendor) => {
  const fromServer = String(vendor ?? "").toUpperCase();
  if (fromServer === EhrVendor.ATHENA) return EhrVendor.ATHENA;
  if (fromServer === EhrVendor.ECW) return EhrVendor.ECW;
  return getEhrVendor(practiceLocationId);
};

/**
 * True when the document can actually be fetched back out of eCW.
 *
 * Notes synced from eCW carry the eCW DocumentReference id as their document
 * id; locally uploaded ones carry a file path, which always has a slash in it.
 *
 * Both C-CDA documents and Patient Documents PDFs are readable. PDFs are
 * discovered through eCW's type=103140-0 feed and downloaded through Binary.
 */
/**
 * Document id prefixes for the PDFs CareVitality generates itself.
 *
 * These mirror EhrDocumentKind in common-classes, which owns the prefix each generated document id
 * is built from. They travel in the opposite direction to everything else here: we push them TO the
 * EHR, so the EHR has no copy to fetch back under this id.
 *
 * Asking for one anyway gets a rejection from ehr-integration naming an unmapped ClinicalDocument
 * and telling the user to "run ClinicalDocument sync first", which is impossible advice for a
 * consent form the EHR never held. The local copy is the only copy, and the CareVitality download
 * beside it is what serves it.
 */
const GENERATED_DOCUMENT_PREFIXES = [
  "careplan-completion-",
  "careplan-recurring-",
  "careplan-summary-",
  "careplan-tcm-",
  "program-consent-",
  "program-unenroll-",
  "patient-readings-",
  "training-notes-",
  "add-on-time-",
];

/** True for a PDF CareVitality generated, which no EHR can serve back under this id. */
export const isGeneratedDocument: (
  documentId: string | null | undefined
) => boolean = (documentId) => {
  if (!documentId) return false;
  const id = documentId.toLowerCase();
  return GENERATED_DOCUMENT_PREFIXES.some((prefix) => id.startsWith(prefix));
};

export const isEcwSourcedDocument: (
  practiceLocationId: number | null | undefined,
  documentId: string | null | undefined,
  vendor?: string | null
) => boolean = (practiceLocationId, documentId, vendor) => {
  if (resolveVendor(practiceLocationId, vendor) !== EhrVendor.ECW) return false;
  if (!documentId) return false;
  if (isGeneratedDocument(documentId)) return false;
  return !documentId.includes("/");
};

/**
 * Whether this document can be fetched back from Athena.
 *
 * Athena keeps clinical documents itself rather than sending the bytes
 * during sync, so the local copy holds metadata only and the EHR is the
 * only place the PDF exists. That is why the "Download EHR source" action
 * has to be offered at all -- without it those documents are unreachable
 * from the portal (CV-448).
 *
 * The id says which endpoint serves it: clinical documents are stored
 * under an `athena-clinicaldocument-` prefix, anything else is a FHIR
 * DocumentReference id.
 *
 * Except for the PDFs we generated ourselves. Those are pushed TO Athena, so Athena holds nothing
 * under the CareVitality id and the request comes back as "ClinicalDocument program-consent-... is
 * not mapped for patient ...; run ClinicalDocument sync first" -- advice that cannot be followed,
 * because no sync will ever produce a consent form the EHR did not originate.
 */
export const isAthenaSourcedDocument: (
  practiceLocationId: number | null | undefined,
  documentId: string | null | undefined,
  vendor?: string | null
) => boolean = (practiceLocationId, documentId, vendor) => {
  if (resolveVendor(practiceLocationId, vendor) !== EhrVendor.ATHENA) return false;
  if (isGeneratedDocument(documentId)) return false;
  return !!documentId;
};

/** True for a document either vendor can serve from the EHR. */
export const isEhrSourcedDocument: (
  practiceLocationId: number | null | undefined,
  documentId: string | null | undefined,
  vendor?: string | null
) => boolean = (practiceLocationId, documentId, vendor) =>
  isEcwSourcedDocument(practiceLocationId, documentId, vendor) ||
  isAthenaSourcedDocument(practiceLocationId, documentId, vendor);

const ATHENA_CLINICAL_DOCUMENT_PREFIX = "athena-clinicaldocument-";

/**
 * Fetches the document bytes from Athena via ehr-integration.
 *
 * Athena answers a failure with HTTP 200 carrying a GenericResponse body
 * rather than an error status, so `response.ok` is not enough on its own:
 * a JSON content type on what should be a document is the real signal.
 * Without that check the caller would hand the user a "PDF" containing an
 * error envelope.
 */
const downloadFromAthena: (
  practiceLocationId: number | null | undefined,
  patientId: string | null | undefined,
  documentId: string
) => Promise<EhrDownloadResult> = async (
  practiceLocationId,
  patientId,
  documentId
) => {
  if (practiceLocationId === null || practiceLocationId === undefined) {
    return { success: false, message: "This practice is not EHR-enabled." };
  }
  if (!patientId) {
    return { success: false, message: "No patient selected." };
  }

  const isClinicalDocument = documentId.startsWith(
    ATHENA_CLINICAL_DOCUMENT_PREFIX
  );
  const segment = isClinicalDocument
    ? "clinical-document"
    : "document-reference";
  const id = isClinicalDocument
    ? documentId.slice(ATHENA_CLINICAL_DOCUMENT_PREFIX.length)
    : documentId;

  const url =
    `${process.env.REACT_APP_BASE_EHR_URL}/ehr-athena` +
    `/patient/${patientId}` +
    `/${segment}/${encodeURIComponent(id)}/download`;

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: { Authorization: bearer() },
    });
    const contentType = response.headers.get("content-type") || "";
    if (!response.ok || contentType.includes("application/json")) {
      let message = `Download failed (${response.status}).`;
      try {
        const body = await response.json();
        message = body?.error || body?.detail || body?.msg || message;
      } catch (e) {
        // keep the status-based message
      }
      return { success: false, message };
    }
    const blob = await response.blob();
    return {
      success: true,
      blob,
      contentType: contentType || "application/octet-stream",
    };
  } catch (e: any) {
    return {
      success: false,
      message: e?.message || "Could not reach the EHR service.",
    };
  }
};

/** Fetches the document bytes from eCW via ehr-integration. */
const downloadFromEcw: (
  practiceLocationId: number | null | undefined,
  patientId: string | null | undefined,
  documentReferenceId: string
) => Promise<EhrDownloadResult> = async (
  practiceLocationId,
  patientId,
  documentReferenceId
) => {
  if (practiceLocationId === null || practiceLocationId === undefined) {
    return { success: false, message: "This practice is not EHR-enabled." };
  }
  if (!patientId) {
    return { success: false, message: "No patient selected." };
  }
  const url =
    `${process.env.REACT_APP_BASE_EHR_URL}/ehr-ecw` +
    `/practice-location/${practiceLocationId}` +
    `/api/patient/${patientId}` +
    `/document-reference/${encodeURIComponent(documentReferenceId)}/download`;

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: { Authorization: bearer() },
    });
    if (!response.ok) {
      // The error body is JSON even though a success is binary.
      let message = `Download failed (${response.status}).`;
      try {
        const body = await response.json();
        if (body?.detail) message = body.detail;
      } catch (e) {
        // keep the status-based message
      }
      return { success: false, message };
    }
    const blob = await response.blob();
    return {
      success: true,
      blob,
      contentType:
        response.headers.get("content-type") || "application/octet-stream",
    };
  } catch (e: any) {
    return {
      success: false,
      message: e?.message || "Could not reach the EHR service.",
    };
  }
};

/** Fetches from whichever EHR the practice uses. */
const downloadFromEhr: (
  practiceLocationId: number | null | undefined,
  patientId: string | null | undefined,
  documentId: string,
  vendor?: string | null
) => Promise<EhrDownloadResult> = (
  practiceLocationId,
  patientId,
  documentId,
  vendor
) =>
  isAthenaSourcedDocument(practiceLocationId, documentId, vendor)
    ? downloadFromAthena(practiceLocationId, patientId, documentId)
    : downloadFromEcw(practiceLocationId, patientId, documentId);

export default {
  downloadFromEcw,
  downloadFromAthena,
  downloadFromEhr,
  isEcwSourcedDocument,
  isAthenaSourcedDocument,
  isEhrSourcedDocument,
};
